import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Collapse,
  CardActions,
  Button,
  Divider,
  Link,
  styled,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { logger } from '../utils/logger';
import { keyframes } from '@mui/system';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/opacity.css';

// Pulse animation for Open Now badge
const pulseKeyframes = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.4);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(22, 163, 74, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(22, 163, 74, 0);
  }
`;

// Styled components for desktop vertical card
const DesktopCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  width: '240px',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 24px rgba(217, 119, 6, 0.15)',
  },
}));

// Styled components for mobile horizontal card
const MobileCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  minHeight: '110px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 2px 8px rgba(45, 42, 38, 0.08)',
}));

// Open Now indicator (small green dot for mobile, badge for desktop)
const OpenNowDot = styled(Box)(({ theme }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  animation: `${pulseKeyframes} 2s infinite`,
  flexShrink: 0,
}));

const OpenNowBadge = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.success.main,
  color: 'white',
  padding: '3px 10px',
  borderRadius: '12px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontWeight: 600,
  fontSize: '0.75rem',
}));

const DealCard = ({ venue }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState(false);
  const [, setImageError] = useState(false);
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const recaptchaRef = useRef(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const {
    headline,
    address,
    main_offer,
    days,
    phone,
    link,
    image_url,
    place_id,
    opening_hours,
  } = venue;

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `https://${url}`;
  };

  // Check if the venue is currently open
  const isOpenNow = () => {
    if (!venue || !opening_hours) {
      return false;
    }

    const now = new Date();
    const day = now.getDay();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const currentTime = hours * 100 + minutes;

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[day];

    const todayHours = opening_hours[dayName] || [];

    return todayHours.some(timeSlot => {
      if (!timeSlot || typeof timeSlot.start !== 'number' || typeof timeSlot.end !== 'number') {
        return false;
      }
      return currentTime >= timeSlot.start && currentTime <= timeSlot.end;
    });
  };

  const openNow = isOpenNow();

  // Get closing time for today
  const getClosingTime = () => {
    if (!opening_hours) return null;
    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[now.getDay()];
    const todayHours = opening_hours[dayName] || [];
    const currentTime = now.getHours() * 100 + now.getMinutes();

    for (const slot of todayHours) {
      if (slot && currentTime >= slot.start && currentTime <= slot.end) {
        const endHour = Math.floor(slot.end / 100);
        const endMin = slot.end % 100;
        return `${endHour}:${endMin.toString().padStart(2, '0')}`;
      }
    }
    return null;
  };

  const handleSuggestionOpen = () => {
    setSuggestionOpen(true);
  };

  const handleSuggestionClose = () => {
    setSuggestionOpen(false);
    setSuggestion('');
    setSubmitError(null);
    setRecaptchaToken(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleRecaptchaChange = (token) => {
    logger.log('reCAPTCHA callback triggered');
    setRecaptchaToken(token);
  };

  const handleRecaptchaError = () => {
    console.error('reCAPTCHA error occurred');
    setSubmitError('Error loading verification. Please try again.');
  };

  const handleSuggestionSubmit = async () => {
    if (!suggestion.trim() || !recaptchaToken) {
      setSubmitError('Please complete the verification');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      logger.log('Submitting suggestion for venue:', venue.id);

      const suggestionData = {
        venue_id: venue.id || '',
        venue_name: venue.headline || '',
        suggestion: suggestion.trim(),
        created_at: serverTimestamp(),
        status: 'pending',
        metadata: {
          admin_url: `/admin/venues/edit/${venue.id}`,
          recaptcha_token: recaptchaToken
        }
      };

      const docRef = await addDoc(collection(db, 'venue_suggestions'), suggestionData);
      logger.log('Suggestion submitted successfully, ID:', docRef.id);

      handleSuggestionClose();
    } catch (error) {
      logger.error('Error submitting suggestion:', error);
      setSubmitError('Failed to submit suggestion. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const closingTime = getClosingTime();

  // Image component used by both layouts
  const VenueImage = ({ width, height, borderRadius = 0 }) => (
    <LazyLoadImage
      src={(image_url && image_url !== 'none') ? image_url : '/images/default_venue_photo.jpg'}
      placeholderSrc="/images/default_venue_photo.jpg"
      effect="opacity"
      height={height}
      width={width}
      onError={() => setImageError(true)}
      alt={headline || 'Venue photo'}
      wrapperProps={{
        style: {
          width: width,
          height: height,
          display: 'block',
          flexShrink: 0,
          borderRadius: borderRadius
        }
      }}
      style={{
        objectFit: 'cover',
        width: '100%',
        height: '100%'
      }}
    />
  );

  // Expandable details content (shared between layouts)
  const ExpandedContent = () => (
    <CardContent sx={{ pt: 2 }}>
      {/* Contact information */}
      <Box sx={{ mb: 2 }}>
        {phone && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
            <Link href={`tel:${phone}`} color="primary" underline="hover">
              {phone}
            </Link>
          </Box>
        )}

        {link && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LanguageIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
            <Link
              href={getFullUrl(link)}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              Website <OpenInNewIcon sx={{ ml: 0.5, fontSize: 14 }} />
            </Link>
          </Box>
        )}
      </Box>

      {/* Google Maps link */}
      <Box sx={{ mt: 2 }}>
        <Link
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(headline + ' ' + address)}${place_id ? `&query_place_id=${place_id}` : ''}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '10px 14px',
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: 2,
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            transition: 'background-color 0.2s',
            '&:hover': {
              backgroundColor: 'primary.dark'
            }
          }}
        >
          <LocationOnIcon sx={{ mr: 1, fontSize: 18 }} />
          View on Google Maps
        </Link>
      </Box>

      {/* Suggestion section */}
      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          startIcon={<EditIcon />}
          onClick={handleSuggestionOpen}
          size="small"
          sx={{ width: '100%' }}
        >
          Suggest an Edit
        </Button>
      </Box>
    </CardContent>
  );

  // Mobile horizontal layout
  if (isMobile) {
    return (
      <>
        <MobileCard
          data-venue-id={venue.id}
          sx={{
            borderRadius: expanded ? '12px 12px 0 0' : '12px',
            borderBottom: expanded ? 'none' : undefined
          }}
        >
          {/* Left: Thumbnail Image */}
          <Box sx={{ width: 100, minHeight: 110, flexShrink: 0 }}>
            <VenueImage width={100} height="100%" borderRadius={expanded ? "12px 0 0 0" : "12px 0 0 12px"} />
          </Box>

          {/* Right: Content */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <CardContent sx={{ py: 1.5, px: 2, flex: 1 }}>
              {/* Header row with name and open indicator */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                <Typography
                  variant="subtitle1"
                  component="h2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: 'text.primary'
                  }}
                >
                  {headline}
                </Typography>
                {openNow && <OpenNowDot />}
              </Box>

              {/* Address */}
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  mb: 0.5
                }}
              >
                {address}
              </Typography>

              {/* Main offer */}
              <Typography
                variant="body2"
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: 1.3,
                  mb: 1
                }}
              >
                {main_offer || 'Happy Hour Deal'}
              </Typography>

              {/* Days/Hours info */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 14, mr: 0.5, mt: 0.25, color: 'text.secondary', flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: 1.4
                  }}
                >
                  {days || 'Check venue for hours'}
                </Typography>
              </Box>

              {/* Meta info row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {venue.distance && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocationOnIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      {venue.distance.toFixed(1)} km
                    </Typography>
                  </Box>
                )}
                {openNow && closingTime && (
                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
                    Open until {closingTime}
                  </Typography>
                )}
              </Box>
            </CardContent>

            {/* Expand button */}
            <Button
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
              endIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              size="small"
              sx={{
                py: 0.5,
                color: 'text.secondary',
                justifyContent: 'center',
                borderTop: '1px solid',
                borderColor: 'divider',
                borderRadius: 0,
                fontSize: '0.75rem'
              }}
            >
              {expanded ? 'Less' : 'More'}
            </Button>
          </Box>
        </MobileCard>

        {/* Expandable section below mobile card */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: '0 0 12px 12px',
              border: '1px solid',
              borderColor: 'divider',
              borderTop: 'none',
              mt: '-1px',
              boxShadow: '0 4px 8px rgba(45, 42, 38, 0.08)'
            }}
          >
            <ExpandedContent />
          </Box>
        </Collapse>

        {/* Suggestion Dialog */}
        <Dialog
          open={suggestionOpen}
          onClose={handleSuggestionClose}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Suggest an Edit</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Help us keep the information accurate and up-to-date by suggesting changes.
            </Typography>
            <TextField
              autoFocus
              multiline
              rows={4}
              fullWidth
              label="Your Suggestion"
              value={suggestion}
              onChange={(e) => setSuggestion(e.target.value)}
              error={Boolean(submitError)}
              helperText={submitError}
              disabled={submitting}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={handleRecaptchaChange}
                onError={handleRecaptchaError}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleSuggestionClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSuggestionSubmit}
              variant="contained"
              disabled={!suggestion.trim() || !recaptchaToken || submitting}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Desktop vertical layout
  return (
    <DesktopCard data-venue-id={venue.id}>
      {/* Image container with overlay */}
      <Box sx={{ position: 'relative', height: 160 }}>
        <VenueImage width="100%" height={160} />

        {/* Gradient overlay with venue name */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            color: 'white',
            p: 1.5,
            backgroundImage: 'linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))'
          }}
        >
          <Typography
            variant="subtitle1"
            component="h2"
            sx={{
              fontWeight: 600,
              fontSize: '0.95rem',
              lineHeight: 1.2,
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
            }}
          >
            {headline}
          </Typography>
        </Box>
      </Box>

      {/* Main content */}
      <CardContent sx={{ flexGrow: 1, py: 1.5, px: 2 }}>
        {/* Address */}
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: '0.8rem',
            mb: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {address}
        </Typography>

        {/* Main offer */}
        <Typography
          sx={{
            color: 'text.primary',
            fontWeight: 500,
            fontSize: '0.85rem',
            minHeight: '3.3rem',
            maxHeight: '3.3rem',
            lineHeight: 1.3,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            mb: 1
          }}
        >
          {main_offer || 'Happy Hour Deal'}
        </Typography>

        {/* Days/Hours info */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
          <AccessTimeIcon sx={{ fontSize: 14, mr: 0.5, mt: 0.25, color: 'text.secondary', flexShrink: 0 }} />
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.4
            }}
          >
            {days || 'Check venue for hours'}
          </Typography>
        </Box>

        {/* Open Now badge */}
        {openNow && (
          <OpenNowBadge sx={{ mb: 1 }}>
            <OpenNowDot sx={{ width: 6, height: 6 }} />
            Open until {closingTime}
          </OpenNowBadge>
        )}

        {/* Distance info */}
        {venue.distance && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocationOnIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {venue.distance.toFixed(1)} km away
            </Typography>
          </Box>
        )}
      </CardContent>

      <Divider />

      {/* Expand/collapse button */}
      <CardActions disableSpacing sx={{ py: 0.5 }}>
        <Button
          onClick={handleExpandClick}
          aria-expanded={expanded}
          aria-label="show more"
          endIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          sx={{
            width: '100%',
            py: 0.5,
            color: 'text.secondary',
            justifyContent: 'center',
            fontSize: '0.8rem'
          }}
        >
          {expanded ? 'Close' : 'Details'}
        </Button>
      </CardActions>

      {/* Expandable details */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <ExpandedContent />
      </Collapse>

      {/* Suggestion Dialog */}
      <Dialog
        open={suggestionOpen}
        onClose={handleSuggestionClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Suggest an Edit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Help us keep the information accurate and up-to-date by suggesting changes.
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            label="Your Suggestion"
            value={suggestion}
            onChange={(e) => setSuggestion(e.target.value)}
            error={Boolean(submitError)}
            helperText={submitError}
            disabled={submitting}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={handleRecaptchaChange}
              onError={handleRecaptchaError}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuggestionClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSuggestionSubmit}
            variant="contained"
            disabled={!suggestion.trim() || !recaptchaToken || submitting}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </DesktopCard>
  );
};

export default DealCard;
